import { countryName } from '../data/countries'
import type { FilterOption } from '../components/filter-trigger'

export interface ICountryGroup<T> {
  /** The country code, or 'none' for the group of locations with no country. */
  key: string
  countryCode: string
  name: string
  items: T[]
}

interface ILocated {
  countryCode?: string | null
  isDefault?: boolean | null
}

export interface ILocationFilterSource {
  id: string
  name: string
  countryCode?: string | null
}

/**
 * Grouping pays off only with two or more countries and at least one of them holding two or more locations: one country
 * needs no heading, and one location per country would put a heading over every location. A location with no country adds none.
 */
export function shouldGroupByCountry(countryCodes: (string | null | undefined)[]): boolean {
  const counts = new Map<string, number>()
  for (const code of countryCodes) {
    if (code) counts.set(code, (counts.get(code) ?? 0) + 1)
  }
  return counts.size > 1 && [...counts.values()].some((count) => count > 1)
}

/**
 * One group per country, each keeping the order the items came in. The default location's country leads, the rest
 * follow A-Z, and the locations with no country go last, where they read as unfinished.
 */
export function groupByCountry<T extends ILocated>(items: T[], noCountryLabel: string): ICountryGroup<T>[] {
  const byCode = new Map<string, T[]>()
  for (const item of items) {
    const code = item.countryCode ?? ''
    byCode.set(code, [...(byCode.get(code) ?? []), item])
  }
  const groups = [...byCode.entries()].map(([countryCode, groupItems]) => ({
    key: countryCode || 'none',
    countryCode,
    name: countryName(countryCode) ?? (countryCode || noCountryLabel),
    items: groupItems,
  }))
  const placed = groups.filter((group) => group.countryCode)
  const lead = placed.filter((group) => group.items.some((item) => item.isDefault))
  const rest = placed.filter((group) => !lead.includes(group)).sort((a, b) => a.name.localeCompare(b.name))
  return [...lead, ...rest, ...groups.filter((group) => !group.countryCode)]
}

/**
 * A location filter's options under a heading per country, by the same rule as the Locations page. The heading is a
 * `group` option: it never enters the value, and its box ticks every location under it. Undefined when the list should
 * stay flat. The order is final, so the filter needs `keepOrder`.
 */
export function locationOptionsByCountry(locations: ILocationFilterSource[], noCountryLabel: string): FilterOption[] | undefined {
  if (!shouldGroupByCountry(locations.map((location) => location.countryCode))) return undefined
  const sorted = [...locations].sort((a, b) => a.name.localeCompare(b.name))
  return groupByCountry(sorted, noCountryLabel).flatMap((group): FilterOption[] => [
    { value: `country:${group.key}`, label: group.name, group: true },
    ...group.items.map((location) => ({ value: location.id, label: location.name, indentLevel: 1 })),
  ])
}
